import Phaser from 'phaser';
import { DPR, UI_DEPTH } from '@constants/common.const';
import { ShadowNineSlice } from './Shadow9Slice';
import { GameConfigService } from '@services/core/GameConfigService';

export type TooltipTextAlign = 'left' | 'center' | 'right';
export type TooltipCaretSide = 'top' | 'bottom' | 'left' | 'right';
export type TooltipCaretAlign = 'start' | 'center' | 'end';

export interface TooltipCaretConfig {
  /** Which edge the caret is attached to. */
  side: TooltipCaretSide;
  /** How the caret is positioned along the attached edge. */
  align?: TooltipCaretAlign;
  /**
   * Optional pixel offset along the attached edge, relative to the chosen align.
   * Example: align='center', offset=10 shifts caret 10px to the right (or down for left/right).
   */
  offset?: number;
}

export interface TooltipConfig {
  scene: Phaser.Scene;
  x: number;
  y: number;
  text: string;
  /** Where the caret should be drawn. Omit to render without a caret. */
  caret?: TooltipCaretConfig;
  /** Text alignment inside the bubble. */
  textAlign?: TooltipTextAlign;
  /** Optional max text width in px (before DPR). Ignored when width is set. */
  maxWidth?: number;
  /** Fixed bubble width in px (before DPR). Text wraps to fill it. */
  width?: number;
  /** Set depth (defaults above UI). */
  depth?: number;

  /** Auto-hide after showing (ms). Default: 3000. Set to 0/false to disable. */
  autoHideDelayMs?: number | false;
  /** Fade-out duration (ms). Default: 250. */
  fadeOutDurationMs?: number;
  /** Dismiss the tooltip immediately when the user taps/clicks it. */
  closeOnClick?: boolean;
  /** Called whenever the tooltip transitions to hidden (auto-hide, click, or manual). */
  onHide?: () => void;
}

const DEFAULT_BG_ALPHA = 1;

// Styles from requirements (scaled by DPR)
const PADDING_X = 16;
const PADDING_Y = 12;
const GAP = 4;
const RADIUS = 16;

const CARET_W = 12;
const CARET_H = 3;

// Rounding for the caret tip (in CSS pixels, scaled by DPR)
const CARET_TIP_RADIUS = 3;

export class Tooltip extends Phaser.GameObjects.Container {
  private bgRT!: Phaser.GameObjects.RenderTexture;
  private label: Phaser.GameObjects.Text;
  private shadow: ShadowNineSlice;

  private currentTextAlign: TooltipTextAlign;
  private currentCaret: Required<TooltipCaretConfig> | null;

  private defaultColor: number;

  // cached layout
  private fixedWidth?: number;
  private bubbleW = 0;
  private bubbleH = 0;

  private autoHideDelayMs: number | false;
  private fadeOutDurationMs: number;
  private onHideCallback?: () => void;

  private autoHideEvent?: Phaser.Time.TimerEvent;
  private fadeTween?: Phaser.Tweens.Tween;

  constructor(config: TooltipConfig) {
    super(config.scene, config.x, config.y);

    this.scene.add.existing(this);
    this.setDepth(config.depth ?? UI_DEPTH + 50);
    this.defaultColor =
      new GameConfigService().getColor('--color-base-1') ?? 0xffffff;

    this.currentTextAlign = config.textAlign ?? 'center';
    this.currentCaret = config.caret
      ? {
          side: config.caret.side,
          align: config.caret.align ?? 'center',
          offset: config.caret.offset ?? 0,
        }
      : null;

    this.autoHideDelayMs = config.autoHideDelayMs ?? 0;
    this.fadeOutDurationMs = config.fadeOutDurationMs ?? 250;
    this.onHideCallback = config.onHide;
    this.fixedWidth = config.width;

    // Background + caret RT — baked on each redraw, no earcut per frame
    this.bgRT = this.scene.add.renderTexture(0, 0, 1, 1);
    this.bgRT.setOrigin(0, 0);

    this.shadow = new ShadowNineSlice(this.scene, 0, 0, 0, 0, 'rounded');

    const wrapWidth =
      config.width != null
        ? (config.width - PADDING_X * 2) * DPR
        : config.maxWidth != null
          ? config.maxWidth * DPR
          : undefined;

    // Text
    this.label = this.scene.add.text(0, 0, config.text, {
      fontFamily: 'korolev, sans-serif',
      fontSize: `${14 * DPR}px`,
      fontStyle: '500',
      color: new GameConfigService().getColorHex('--color-neutral-100'),
      align: this.currentTextAlign,
      resolution: DPR,
      wordWrap: wrapWidth
        ? { width: wrapWidth, useAdvancedWrap: true }
        : undefined,
    });

    this.add([this.shadow, this.bgRT, this.label]);

    this.redraw();

    if (config.closeOnClick) {
      this.setInteractive();
      this.on(Phaser.Input.Events.POINTER_DOWN, () => this.fadeOutAndHide());
    }
  }

  /** Update tooltip text and relayout/redraw. */
  public setText(text: string): this {
    this.label.setText(text);
    this.redraw();
    return this;
  }

  /** Add or update the caret and redraw. */
  public setCaret(config: TooltipCaretConfig): this {
    this.currentCaret = {
      side: config.side,
      align: config.align ?? 'center',
      offset: config.offset ?? 0,
    };
    this.redraw();
    return this;
  }

  /** Remove the caret entirely and redraw. */
  public removeCaret(): this {
    if (this.currentCaret === null) return this;
    this.currentCaret = null;
    this.redraw();
    return this;
  }

  /** A convenience to show/hide without destroying. */
  public show(): this {
    this.cancelAutoHide();
    this.cancelFade();

    this.setAlpha(1);
    this.setVisible(true);
    this.setActive(true);

    this.scheduleAutoHide();

    return this;
  }

  public hide(): this {
    this.cancelAutoHide();
    this.cancelFade();

    this.setVisible(false);
    this.setActive(false);
    this.onHideCallback?.();
    return this;
  }

  /** Fade out now, then hide. Safe to call multiple times. */
  public fadeOutAndHide(durationMs: number = this.fadeOutDurationMs): this {
    this.cancelAutoHide();
    this.cancelFade();

    // If scene is gone or tween manager unavailable, just hide.
    if (!this.scene || !this.scene.tweens) {
      return this.hide();
    }

    this.fadeTween = this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration: durationMs,
      ease: 'Linear',
      onComplete: () => {
        // Avoid leaving it invisible but interactive/active.
        this.hide();
        this.setAlpha(1);
      },
    });

    return this;
  }

  public override destroy(fromScene?: boolean): void {
    this.cancelAutoHide();
    this.cancelFade();
    super.destroy(fromScene);
  }

  private scheduleAutoHide(): void {
    if (this.autoHideDelayMs === false || this.autoHideDelayMs <= 0) return;

    // If scene is shutting down, don't schedule.
    if (!this.scene || !this.scene.time) return;

    this.autoHideEvent = this.scene.time.delayedCall(
      this.autoHideDelayMs,
      () => {
        // Tooltip might have been hidden/destroyed before the timer fires.
        if (!this.active || !this.visible) return;
        this.fadeOutAndHide(this.fadeOutDurationMs);
      },
    );
  }

  private cancelAutoHide(): void {
    if (!this.autoHideEvent) return;
    this.autoHideEvent.destroy();
    this.autoHideEvent = undefined;
  }

  private cancelFade(): void {
    if (!this.fadeTween) return;
    this.fadeTween.stop();
    this.fadeTween = undefined;
  }

  private redraw(): void {
    // Measure text
    // Ensure bounds are up-to-date after style/text changes
    const textBounds = this.label.getBounds();
    const textW = textBounds.width;
    const textH = textBounds.height;

    const paddingX = PADDING_X * DPR;
    const paddingY = PADDING_Y * DPR;
    const gap = GAP * DPR;
    const radius = RADIUS * DPR;

    const caretW = CARET_W * DPR;
    const caretH = CARET_H * DPR;

    // Bubble size: fixed width when specified, otherwise content-driven.
    this.bubbleW =
      this.fixedWidth != null
        ? Math.ceil(this.fixedWidth * DPR)
        : Math.ceil(textW + paddingX * 2);
    this.bubbleH = Math.ceil(textH + paddingY * 2);

    // Total size (bubble + caret+gap on one side)
    const totalW =
      this.bubbleW +
      (this.currentCaret?.side === 'left' || this.currentCaret?.side === 'right'
        ? caretH + gap
        : 0);
    const totalH =
      this.bubbleH +
      (this.currentCaret?.side === 'top' || this.currentCaret?.side === 'bottom'
        ? caretH + gap
        : 0);

    // Container origin is its position; we treat that position as CENTER of the total tooltip.
    this.setSize(totalW, totalH);

    // Local top-left of the total tooltip
    const left = -totalW / 2;
    const top = -totalH / 2;

    // Bubble top-left within the total tooltip
    const bubbleX =
      left + (this.currentCaret?.side === 'left' ? caretH + gap : 0);
    const bubbleY =
      top + (this.currentCaret?.side === 'top' ? caretH + gap : 0);

    this.shadow.setSize(this.bubbleW, this.bubbleH);
    this.shadow.setPosition(0, 0);

    // Layout text inside bubble based on align
    const textXLeft = bubbleX + paddingX;
    const textXCenter = bubbleX + this.bubbleW / 2;
    const textXRight = bubbleX + this.bubbleW - paddingX;

    let labelX = textXCenter;
    let labelOriginX = 0.5;
    if (this.currentTextAlign === 'left') {
      labelX = textXLeft;
      labelOriginX = 0;
    } else if (this.currentTextAlign === 'right') {
      labelX = textXRight;
      labelOriginX = 1;
    }

    const labelY = bubbleY + this.bubbleH / 2;
    this.label.setOrigin(labelOriginX, 0.5);
    this.label.setPosition(labelX, labelY);

    // Bake bubble + caret into RT — no earcut per frame
    const ox = totalW / 2;
    const oy = totalH / 2;
    this.bgRT.setPosition(-totalW / 2, -totalH / 2);
    this.bgRT.resize(totalW, totalH);
    this.bgRT.clear();

    const gfx = this.scene.add.graphics();
    gfx.fillStyle(this.defaultColor, DEFAULT_BG_ALPHA);
    gfx.fillRoundedRect(
      bubbleX + ox,
      bubbleY + oy,
      this.bubbleW,
      this.bubbleH,
      radius,
    );

    if (this.currentCaret !== null) {
      const caret = this.computeCaretTriangle({
        bubbleX,
        bubbleY,
        bubbleW: this.bubbleW,
        bubbleH: this.bubbleH,
        caretW,
        caretH,
      });
      this.drawCaretWithRoundedTip(gfx, caret, ox, oy);
    }

    this.bgRT.draw(gfx, 0, 0);
    gfx.destroy();
  }

  private drawCaretWithRoundedTip(
    g: Phaser.GameObjects.Graphics,
    caret: {
      ax: number;
      ay: number;
      bx: number;
      by: number;
      cx: number;
      cy: number;
    },
    ox: number,
    oy: number,
  ): void {
    const r = CARET_TIP_RADIUS * DPR;
    if (r <= 0.5) {
      this.drawSharpCaret(g, caret, ox, oy);
      return;
    }

    // Build triangle path
    g.beginPath();
    g.moveTo(caret.ax + ox, caret.ay + oy);
    g.lineTo(caret.bx + ox, caret.by + oy);
    g.lineTo(caret.cx + ox, caret.cy + oy);
    g.closePath();

    // Fill first
    g.fillStyle(this.defaultColor, DEFAULT_BG_ALPHA);
    g.fillPath();

    // Stroke on top to create rounded tip
    g.lineStyle(
      r * 2, // thickness controls roundness
      this.defaultColor,
      DEFAULT_BG_ALPHA,
    );
    g.strokePath();
  }

  private drawSharpCaret(
    g: Phaser.GameObjects.Graphics,
    caret: {
      ax: number;
      ay: number;
      bx: number;
      by: number;
      cx: number;
      cy: number;
    },
    ox: number,
    oy: number,
  ): void {
    g.beginPath();
    g.moveTo(caret.ax + ox, caret.ay + oy);
    g.lineTo(caret.bx + ox, caret.by + oy);
    g.lineTo(caret.cx + ox, caret.cy + oy);
    g.closePath();
    g.fillPath();
  }
  private computeCaretTriangle(params: {
    bubbleX: number;
    bubbleY: number;
    bubbleW: number;
    bubbleH: number;
    caretW: number;
    caretH: number;
  }): {
    ax: number;
    ay: number;
    bx: number;
    by: number;
    cx: number;
    cy: number;
  } {
    const { bubbleX, bubbleY, bubbleW, bubbleH, caretW, caretH } = params;

    const gap = GAP * DPR;
    const side = this.currentCaret!.side;

    const halfBase = caretW / 2;

    const offset = (this.currentCaret!.offset ?? 0) * DPR;

    // Helper: clamp caret base so it stays within bubble bounds (leaves small margin)
    const clamp = (v: number, min: number, max: number) =>
      Math.max(min, Math.min(max, v));

    if (side === 'top' || side === 'bottom') {
      const baseY = side === 'top' ? bubbleY : bubbleY + bubbleH;
      const tipY =
        side === 'top'
          ? bubbleY - gap - caretH
          : bubbleY + bubbleH + gap + caretH;

      // base center X
      let baseCenterX = bubbleX + bubbleW / 2;
      if (this.currentCaret!.align === 'start')
        baseCenterX = bubbleX + halfBase;
      if (this.currentCaret!.align === 'end')
        baseCenterX = bubbleX + bubbleW - halfBase;
      baseCenterX += offset;

      baseCenterX = clamp(
        baseCenterX,
        bubbleX + halfBase,
        bubbleX + bubbleW - halfBase,
      );

      // Triangle points: A=tip, B/C=base corners
      return {
        ax: baseCenterX,
        ay: tipY,
        bx: baseCenterX - halfBase,
        by: baseY,
        cx: baseCenterX + halfBase,
        cy: baseY,
      };
    }

    // left/right
    const baseX = side === 'left' ? bubbleX : bubbleX + bubbleW;
    const tipX =
      side === 'left'
        ? bubbleX - gap - caretH
        : bubbleX + bubbleW + gap + caretH;

    let baseCenterY = bubbleY + bubbleH / 2;
    if (this.currentCaret!.align === 'start') baseCenterY = bubbleY + halfBase;
    if (this.currentCaret!.align === 'end')
      baseCenterY = bubbleY + bubbleH - halfBase;
    baseCenterY += offset;

    baseCenterY = clamp(
      baseCenterY,
      bubbleY + halfBase,
      bubbleY + bubbleH - halfBase,
    );

    return {
      ax: tipX,
      ay: baseCenterY,
      bx: baseX,
      by: baseCenterY - halfBase,
      cx: baseX,
      cy: baseCenterY + halfBase,
    };
  }
}
